/**
 * Client-side attention reporting (tdd-0079 phase 4, client half).
 *
 * The server can see that a connection exists and that awareness updates are
 * arriving. It cannot see **focus**, and awareness misses whole classes of
 * present user. This closes both gaps from the one place that knows: the tab.
 *
 * ## Why awareness alone is not enough
 *
 * Awareness fires on cursor and selection movement, so it misses:
 *
 *  - someone **reading** a long document, scrolling with a trackpad and never
 *    moving a text cursor;
 *  - **touch devices**, where there is no mouse movement at all;
 *  - anyone navigating by keyboard alone.
 *
 * All three are unambiguously present, and all three would be classified
 * `passive` on awareness alone — a bell badge for the paragraph in front of
 * them. So this listens for a broad input set, not just pointer movement.
 *
 * ## Why a backgrounded tab is the whole point
 *
 * A backgrounded tab keeps its WebSocket open, so every server-side signal says
 * "present". `visibilitychange` is the only thing that distinguishes a user
 * reading this document from one who left it open three hours ago behind a
 * dozen other tabs.
 *
 * ## Cheap to gather, cheap to send
 *
 * Listeners are passive and only touch a local timestamp — a scroll handler
 * that emitted a frame per event would be a performance bug wearing a presence
 * costume. Reports are throttled hard, and sent only when the state they imply
 * would actually change.
 */

/** What the page tells the server about the user's attention. */
export interface AttentionReport {
  /** Window focused *and* the document visible. */
  focused: boolean
  at: number
}

export interface AttentionMonitorConfig {
  /** Called when a report should go out. Wire to `provider.sendStateless`. */
  onReport: (report: AttentionReport) => void
  /**
   * Minimum gap between reports while the user is continuously active
   * (default 60s).
   *
   * Matches the server's own awareness throttle. Presence horizons are tens of
   * minutes, so a report a minute is ample resolution.
   */
  throttleMs?: number
  /** Injected in tests. */
  now?: () => number
  /** Injected in tests; defaults to the real document/window. */
  target?: AttentionTarget
}

/** The slice of the DOM this needs, so tests need no jsdom. */
export interface AttentionTarget {
  addEventListener(type: string, handler: () => void, opts?: { passive?: boolean }): void
  removeEventListener(type: string, handler: () => void): void
  /** `document.visibilityState === "visible"` and the window has focus. */
  isVisible(): boolean
}

/**
 * Input events that count as attention.
 *
 * `scroll` and `touchstart` are the load-bearing additions over what awareness
 * already sees — they are what a reader on a trackpad or a phone actually
 * produces. `pointerdown` covers stylus and mouse alike; `keydown` covers
 * keyboard-only navigation.
 *
 * Deliberately NOT `mousemove` or `pointermove`: they fire continuously, add
 * nothing awareness does not already report, and would make the throttle the
 * only thing standing between this and a busy loop.
 */
const INPUT_EVENTS = ["scroll", "click", "keydown", "touchstart", "pointerdown"] as const

export interface AttentionMonitor {
  /** Stop listening. */
  stop: () => void
  /** Current focus state, for tests and diagnostics. */
  isFocused: () => boolean
}

export function createAttentionMonitor(config: AttentionMonitorConfig): AttentionMonitor {
  const throttleMs = config.throttleMs ?? 60_000
  const now = config.now ?? (() => Date.now())
  const target = config.target ?? browserTarget()

  let focused = target.isVisible()
  let lastReportAt = 0

  function report(force: boolean): void {
    const at = now()
    // `force` is for a state CHANGE — losing or regaining focus — which must go
    // out immediately. Throttling that would leave the server believing someone
    // is attending a tab they just switched away from, for up to a minute.
    if (!force && at - lastReportAt < throttleMs) return
    lastReportAt = at
    config.onReport({ focused, at })
  }

  function onInput(): void {
    // Input while hidden is possible (a background tab receiving synthetic
    // events) and is not evidence of attention, so it does not resurrect focus.
    if (!focused) return
    report(false)
  }

  function onVisibility(): void {
    const nextFocused = target.isVisible()
    if (nextFocused === focused) return
    focused = nextFocused
    // Both directions are urgent. Gaining focus should stop a push mid-session;
    // losing it should let one through.
    report(true)
  }

  for (const event of INPUT_EVENTS) {
    target.addEventListener(event, onInput, { passive: true })
  }
  target.addEventListener("visibilitychange", onVisibility)
  target.addEventListener("focus", onVisibility)
  target.addEventListener("blur", onVisibility)

  // Report the initial state rather than letting the server assume. Its default
  // is `focused: false` (ambiguity resolves toward delivering), so a user who
  // opens a document and reads without touching anything would otherwise sit at
  // `passive` until their first input — which for a reader may be never.
  report(true)

  return {
    stop() {
      for (const event of INPUT_EVENTS) target.removeEventListener(event, onInput)
      target.removeEventListener("visibilitychange", onVisibility)
      target.removeEventListener("focus", onVisibility)
      target.removeEventListener("blur", onVisibility)
    },
    isFocused: () => focused,
  }
}

/**
 * The real browser target.
 *
 * Visibility and focus are both required: a visible-but-unfocused window (a
 * second monitor, a split view) is a genuinely ambiguous case, and this design
 * resolves ambiguity toward delivering — so it counts as not attending.
 */
function browserTarget(): AttentionTarget {
  return {
    addEventListener(type, handler, opts) {
      const el: EventTarget = type === "focus" || type === "blur" ? window : document
      el.addEventListener(type, handler, opts)
    },
    removeEventListener(type, handler) {
      const el: EventTarget = type === "focus" || type === "blur" ? window : document
      el.removeEventListener(type, handler)
    },
    isVisible() {
      return document.visibilityState === "visible" && document.hasFocus()
    },
  }
}
